import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import ProviderTrackingMap from '@/components/map/ProviderTrackingMap';
import useBookingTracking from '@/hooks/useBookingTracking';
import useServiceStatus from '@/hooks/useServiceStatus';
import { trackingDistanceDisplay, trackingEtaDisplay, trackingStatusLabel } from '@/lib/maps';
import { formatCurrency, formatTimerSeconds } from '@/lib/serviceTimer';

export default function ConsumerBookingTracking() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const inService = booking?.status === 'in_progress';
  const trackingEnabled = booking?.status === 'confirmed' && !inService;
  const serviceEnabled =
    booking?.status === 'confirmed' || inService || booking?.status === 'completed';
  const { tracking, networkOffline: trackingOffline } = useBookingTracking(bookingId, trackingEnabled);
  const { service, timer, extraView, networkOffline: serviceOffline } = useServiceStatus(bookingId, serviceEnabled);
  const networkOffline = trackingOffline || serviceOffline;

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(({ data }) => setBooking(data.booking)).catch(() => {});
  }, [bookingId]);

  if (!booking) {
    return (
      <MobileShell title="Booking">
        <div className="p-5 text-sm text-slate-500">Loading…</div>
      </MobileShell>
    );
  }

  const statusLabel = booking.status === 'completed'
    ? 'Service completed'
    : inService && service?.service_phase === 'finished'
      ? 'Awaiting extra payment'
      : inService
        ? timer?.phase === 'extra_time'
          ? 'Extra time'
          : 'Service in progress'
        : tracking
          ? trackingStatusLabel(tracking)
          : 'Loading…';
  const etaDisplay = tracking ? trackingEtaDisplay(tracking) : null;
  const distanceLabel = tracking ? trackingDistanceDisplay(tracking) : null;
  const arrived = service?.travel_status === 'arrived' && booking.status === 'confirmed';
  const showOtp = service?.start_otp && arrived;

  return (
    <MobileShell title="Your booking">
      <div className="px-5 py-5 space-y-4" data-testid="consumer-tracking-page">
        <div className="resqly-card p-4">
          <div className="text-sm font-semibold text-slate-800 capitalize">
            {booking.service_key?.replace(/_/g, ' ')}
          </div>
          <div data-testid="tracking-status-label" className="text-sm text-slate-600 mt-1">
            {statusLabel}
          </div>
          {tracking?.provider && !inService && (
            <div className="mt-2 text-sm text-slate-700">
              {tracking.provider.name}
              {tracking.provider.rating ? ` · ★ ${tracking.provider.rating}` : ''}
            </div>
          )}
        </div>

        {inService && timer && service?.service_phase !== 'finished' && (
          <div className="resqly-card p-4 space-y-2" data-testid="consumer-service-timer">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {timer.phase === 'extra_time' ? 'EXTRA TIME' : 'SERVICE IN PROGRESS'}
            </div>
            {timer.phase === 'normal_time' && (
              <>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">
                  {formatTimerSeconds(timer.displaySeconds)}
                </div>
                <div className="text-xs text-slate-500">Time remaining</div>
              </>
            )}
            {timer.phase === 'extra_time' && extraView?.mode === 'buffer' && (
              <>
                <div className="text-sm text-amber-700">Included service time has ended</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="consumer-buffer-timer">
                  {formatTimerSeconds(extraView.bufferRemainingSeconds)}
                </div>
                <div className="text-xs text-slate-500">Free buffer remaining</div>
              </>
            )}
            {timer.phase === 'extra_time' && extraView?.mode === 'chargeable' && (
              <div data-testid="consumer-extra-charge" className="space-y-2">
                <div className="text-sm text-amber-700">Extra time</div>
                <div className="text-sm text-slate-600">
                  Chargeable extra time: {formatTimerSeconds(extraView.chargeableSeconds)}
                </div>
                <div className="space-y-1 text-sm border-t border-slate-100 pt-2">
                  <div className="flex justify-between text-slate-700">
                    <span>Extra service</span>
                    <span>{formatCurrency(extraView.extraServiceAmount, extraView.currency)}</span>
                  </div>
                  <div className="flex justify-between text-slate-700" data-testid="consumer-extra-tax">
                    <span>
                      Tax{extraView.extraTaxPercent != null ? ` (${extraView.extraTaxPercent}%)` : ''}
                    </span>
                    <span>{formatCurrency(extraView.extraTaxAmount ?? 0, extraView.currency)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-slate-900" data-testid="consumer-extra-total">
                    <span>Total additional payment</span>
                    <span>{formatCurrency(extraView.extraTotalAmount, extraView.currency)}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500">Estimate only — payment not due yet</div>
              </div>
            )}
          </div>
        )}

        {(service?.service_phase === 'finished' || booking.status === 'completed') && extraView?.mode === 'frozen' && (
          <div className="resqly-card p-4 space-y-3" data-testid="consumer-frozen-settlement">
            <div className="text-sm font-semibold text-slate-900">Final extra charges</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-700">
                <span>Extra service</span>
                <span>{formatCurrency(extraView.extraServiceAmount, extraView.currency)}</span>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>
                  Tax{extraView.extraTaxPercent != null ? ` (${extraView.extraTaxPercent}%)` : ''}
                </span>
                <span>{formatCurrency(extraView.extraTaxAmount ?? 0, extraView.currency)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-900 border-t pt-2">
                <span>Total additional payment</span>
                <span>{formatCurrency(extraView.extraTotalAmount, extraView.currency)}</span>
              </div>
            </div>
            {booking.extra_payment_required &&
              !booking.extra_payment_completed_at &&
              booking.status !== 'completed' && (
                <Link to={`/consumer/bookings/${bookingId}/extra-payment`}>
                  <Button data-testid="consumer-pay-extra-btn" className="w-full bg-slate-900 hover:bg-slate-800">
                    Pay extra charges
                  </Button>
                </Link>
              )}
            {booking.status === 'completed' && (
              <div className="text-xs text-emerald-700">Service completed — thank you!</div>
            )}
            {booking.review_available && (
              <div className="text-xs text-slate-500">This booking is available for review.</div>
            )}
          </div>
        )}

        {showOtp && (
          <div className="resqly-card p-4 space-y-2" data-testid="consumer-start-otp">
            <div className="text-sm font-semibold text-slate-900">Service provider has arrived</div>
            <div className="text-sm text-slate-600">Your service is ready to begin.</div>
            <div className="text-xs text-slate-500">Start OTP</div>
            <div className="text-2xl font-bold tracking-[0.3em] text-slate-900">{service.start_otp}</div>
            <div className="text-xs text-slate-500">Share this code with your provider.</div>
          </div>
        )}

        {networkOffline && tracking && (
          <div
            data-testid="tracking-offline-notice"
            className="resqly-card p-3 text-xs text-amber-800 bg-amber-50 border border-amber-100"
          >
            Connection lost — showing last known location
          </div>
        )}

        {trackingEnabled && tracking && (
          <>
            <div className="resqly-card p-4 space-y-2">
              {etaDisplay && (
                <div data-testid="tracking-eta">
                  {etaDisplay.unavailable ? (
                    <div className="text-sm font-semibold text-slate-700">{etaDisplay.heading}</div>
                  ) : (
                    <>
                      <div className="text-xs text-slate-500">{etaDisplay.heading}</div>
                      <div className="text-2xl font-bold text-slate-900">{etaDisplay.minutes} min</div>
                    </>
                  )}
                </div>
              )}
              {distanceLabel && (
                <div data-testid="tracking-distance" className="text-sm text-slate-600">
                  {distanceLabel}
                </div>
              )}
              {tracking.location_status === 'stale' && (
                <div data-testid="tracking-stale" className="text-sm text-amber-700">
                  Location updating…
                </div>
              )}
            </div>
            <ProviderTrackingMap tracking={tracking} />
          </>
        )}

        <Link to="/consumer/home" className="block text-center text-sm text-blue-700 font-semibold py-2">
          Back to Home
        </Link>
      </div>
    </MobileShell>
  );
}
